/* eslint-disable no-self-assign */
// React imports
import { useState, useRef, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// Material UI imports
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

// Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare, faPlane, faShip, faTruck } from '@fortawesome/free-solid-svg-icons';
import { faHouse } from '@fortawesome/pro-light-svg-icons';
import { faCircleXmark, faCircleCheck } from '@fortawesome/free-regular-svg-icons';

// Local imports
import { calcChargeableWeight, calcWeightOrMeasurment } from './CalcWeight.js';
import { calcOfferSellAndCost } from './utils/opportunityFinancials.js';
import currencyToText from './CurrencyToText.js';
import readableId from './ReadableId.js';
import { statusToText } from './OpportunityRow.utils.js';
import PickOffersAndMarkWonDialog from './dashboard/components/PickOffersAndMarkWonDialog.js';
import LostReasonDialog from './dashboard/components/LostReasonDialog.js';

dayjs.extend(duration)
dayjs.extend(relativeTime)

function OpportunityRow({ opportunity, onOpen, accesstoken, onUpdateOpportunity, accounts, organization, activeTagFilters, exchangeRates }) {
    const edited = _.findLast(opportunity.log || [], l => l.action === 'modified' && JSON.parse(l.params || '{}').previous);
    const theme = useTheme();

    // Calculate profit bar data
    const profitData = (() => {
        if (!exchangeRates || !organization) return null;
        const cur = organization.defaults?.currency || 'USD';
        const selectedOffer = opportunity.offers?.find(o => opportunity.selected?.includes(o.id)) || opportunity.offers?.[0];
        if (!selectedOffer) return null;
        const { sell, cost, gp } = calcOfferSellAndCost({ offer: selectedOffer, opportunity, exchangeRates, organization });
        const margin = sell > 0 ? (gp / sell) * 100 : 0;
        if (sell === 0 && cost === 0) return null;
        if (!isFinite(sell) || !isFinite(cost) || isNaN(sell) || isNaN(cost)) return null;
        const fmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        return { sell, cost, gp, margin, cur, fmt, gpPositive: gp >= 0 };
    })();

    const sums = _(opportunity.products)
        .map((p, i) => {
            const CW = calcChargeableWeight({
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products, round: organization.airRoundChargeableWeight
            });
            if (CW) {
                // Convert all chargeable weights to kg
                let valInKg = CW.val;
                if (CW.postfix === 'lbs') {
                    valInKg = CW.val * 0.453592; // Convert lbs to kg
                }
                return { type: 'chargeable Weight', value: { val: valInKg, postfix: 'kg' } };
            }

            const WM = calcWeightOrMeasurment({
                product: p, transportationMethod: opportunity.transportationMethod, allProducts: opportunity.products
            });
            if (WM) {
                // Convert weight or measurement to a single unit (ton)
                // For volume (cbm), convert to ton (1 cbm = 1 ton for freight calculations)
                // For weight (ton), keep as is
                let valInTon = WM.val;
                if (WM.postfix === 'cbm') {
                    valInTon = WM.val; // 1 cbm = 1 ton for freight calculations
                }
                return { type: 'weight or measurement', value: { val: valInTon, postfix: 'ton' } };
            }

            // A product matching none of the cargo types above (e.g. an Air freight
            // product on a sea quote) has no container quantity — the unified
            // 'Air freight'/'LCL' types keep it inside calculations[]. Skip it rather
            // than counting it as a container with no value, which made sumBy return
            // undefined and blow up on toLocaleString below.
            if (_.isUndefined(p.quantity)) return null;
            return { type: 'Containers', value: { val: p.quantity, postfix: '' } };
        }).compact()
        .groupBy(s => `${s.type}|${s.value.postfix}`)
        .mapValues(v => _.sumBy(v, v2 => v2.value.val || 0))
        .value();

    const sumsText = `<b>${readableId(opportunity)}</b> | ` + _.map(sums, (v, k) => {
        const [type, postfix] = k.split('|');
        return `Total ${type}: <b>${v.toLocaleString()} ${postfix}</b>`;
    }).join(' | ');

    const containerRef = useRef(null);
    const [isNarrow, setIsNarrow] = useState(false);

    useEffect(() => {
      if (!containerRef.current) return;
      const ro = new ResizeObserver(([entry]) => {
        setIsNarrow(entry.contentRect.width < 600);
      });
      ro.observe(containerRef.current);
      return () => ro.disconnect();
    }, []);

    const accountName = account({ opportunity, accounts })?.name || '';
    if (!accountName) return null;

    return (
        <Paper ref={containerRef} sx={{ marginTop: '24px', border: `1px solid ${theme.palette.border1}`, background: theme.palette.panel1 }} elevation={0}>
            <CardHeader
                opportunity={opportunity}
                onOpen={onOpen}
                accesstoken={accesstoken}
                onUpdateOpportunity={onUpdateOpportunity}
                accounts={accounts}
                isNarrow={isNarrow}
            />
            <Box sx={{ padding: '6px', display: 'flex', fontWeight: 500 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, color: theme.palette.text.secondary }}>
                    <Box sx={{ color: theme.palette.text.primary }}>
                        <DescriptionToNode opportunity={opportunity} />
                    </Box>
                    <div style={{ display: 'flex', padding: '6px', whiteSpace: 'nowrap', flexWrap: 'wrap' }}>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                {sumsText.length > 0 && (
                                    <span dangerouslySetInnerHTML={{ __html: sumsText }} />
                                )}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Created {(Date.now() - opportunity.created) < 7 * 24 * 60 * 60 * 1000 && <><b>{dayjs(opportunity.created).format('dddd')}</b>, </>}<b>{dayjs.duration((opportunity.created - new Date().getTime()) / 1000, 'seconds').humanize(true)}</b>
                            </Typography>
                        </div>
                        {edited && (
                            <div>
                                <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                    &nbsp;|&nbsp;
                                </Typography>
                            </div>
                        )}
                        {edited && (
                            <div>
                                <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                    Last edited <b>{dayjs.duration((edited.timestamp - new Date().getTime()) / 1000, 'seconds').humanize(true)}</b>
                                </Typography>
                            </div>
                        )}
                        {opportunity.createdBy && (
                            <div>
                                <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                    &nbsp;|&nbsp;
                                </Typography>
                            </div>
                        )}
                        {opportunity.createdBy && (
                            <div>
                                <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                    Created by <b>{opportunity.createdBy}</b>
                                </Typography>
                            </div>
                        )}
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                &nbsp;|&nbsp;
                            </Typography>
                        </div>
                        <div>
                            <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                Owner is <b>{opportunity.owner}</b>
                            </Typography>
                        </div>
                    </div>
                    {profitData && (
                        <div style={{ padding: '2px 6px' }}>
                            <Typography variant='caption' sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                                Sell: <b>{currencyToText(profitData.cur, true, profitData.fmt.format(profitData.sell))}</b> | Cost: <b>{currencyToText(profitData.cur, true, profitData.fmt.format(profitData.cost))}</b> | GP: <b style={{ color: profitData.gpPositive ? '#2e7d32' : '#d32f2f' }}>{currencyToText(profitData.cur, true, profitData.fmt.format(profitData.gp))}</b> | Margin: <b style={{ color: profitData.gpPositive ? '#2e7d32' : '#d32f2f' }}>{profitData.margin.toFixed(1)}%</b>
                            </Typography>
                        </div>
                    )}
                </Box>
                {!isNarrow && (
                    <Button variant='text' onClick={() => onOpen({ opportunity, newWindow: true })} sx={{ marginTop: '6px', height: '24px', textTransform: 'none' }}>
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                        &nbsp;
                        Open in new window
                    </Button>
                )}
            </Box>
            {!isNarrow && (() => {
                const oppTags = opportunity.tags || [];
                const account = (accounts || []).find(a => a && a.id === opportunity.account?.id);
                const accountTags = (account?.tags || []).filter(t => !t.label?.startsWith('Pipeline:'));
                const hasActiveFilter = activeTagFilters && activeTagFilters.length > 0;

                // Find which account tags matched the filter but aren't on the opportunity itself
                const matchedAccountTags = hasActiveFilter
                    ? accountTags.filter(at => activeTagFilters.some(ft => _.isEqual(ft, at)) && !oppTags.some(ot => _.isEqual(ot, at)))
                    : [];

                if (oppTags.length === 0 && matchedAccountTags.length === 0) return null;

                return (
                    <Box sx={{ padding: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {oppTags.map(tag => (
                            <Chip
                                key={tag.label}
                                label={tag.label}
                                size='small'
                                sx={{
                                    ...(hasActiveFilter && activeTagFilters.some(ft => _.isEqual(ft, tag))
                                        ? { borderColor: '#3b82f6', borderWidth: '2px', borderStyle: 'solid' }
                                        : {})
                                }}
                            />
                        ))}
                        {matchedAccountTags.map(tag => (
                            <Chip
                                key={`acct-${tag.label}`}
                                label={`${tag.label} (account)`}
                                size='small'
                                variant='outlined'
                                sx={{ borderColor: '#3b82f6', borderWidth: '2px', fontStyle: 'italic', fontSize: '11px' }}
                            />
                        ))}
                    </Box>
                );
            })()}
        </Paper>
    );
}

export default OpportunityRow;
export { CardHeader, DescriptionToNode };

function CardHeader({ opportunity, onOpen, accesstoken, onUpdateOpportunity, accounts, isNarrow }) {
    const [ markingWon, setMarkingWon ] = useState(false);
    const [ markingLost, setMarkingLost ] = useState(false);
    const [ askLostReason, setAskLostReason ] = useState(false);
    const [ message, setMessage ] = useState(false);
    // Only the quote's owner (or Monada support) marks it Won or Lost, as the server enforces.
    const { me } = useOutletContext() || {};
    const owner = opportunity.owner || opportunity.createdBy;
    const canChangeStatus = !me?.email || me.email === owner || me.email === 'admin@monada.ai';
    const ownerOnly = what => `Only the quote's owner${owner ? ` (${owner})` : ''} can mark it as ${what}.`;
    const statusError = (e, what) => (e?.response?.status === 401
        ? `${ownerOnly(what)} Ask them, or take ownership of the quote first.`
        : `Couldn't mark the quote as ${what}${e?.response?.data?.detail ? `: ${e.response.data.detail}` : ''}. Please try again.`);
    const [ pickOffers, setPickOffers ] = useState(false);
    const theme = useTheme();

    async function onMarkWon() {
        if (opportunity.offers?.length > 1) {
            setPickOffers(true);
        } else {
            await onMarkWonSingle();
        }
    }

    async function onMarkWonSingle() {
        setMarkingWon(true);

        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&accesstoken=${accesstoken}`, {
                status: 'completed',
            });
            onUpdateOpportunity({ opportunity: _opportunity });
            setMarkingWon(false);
        } catch (e) {
            setMessage({ open: true, message: statusError(e, 'Won') });
            setMarkingWon(false);
        }
    }

    async function onMarkLost(lostReason) {
        setMarkingLost(true);

        try {
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?organizationId=${opportunity.organizationId}&accesstoken=${accesstoken}`, {
                status: 'lost',
                lostReason: _.omit(lostReason, 'offerId'),
                ...(lostReason?.offerId ? { lostOffer: lostReason.offerId } : {}),
            });
            onUpdateOpportunity({ opportunity: _opportunity });
            setMarkingLost(false);
            setAskLostReason(false);
        } catch (e) {
            setAskLostReason(false);
            setMessage({ open: true, message: statusError(e, 'Lost') });
            setMarkingLost(false);
        }
    }

    function handleSnackbarClose() {
        setMessage({ ...message, open: false });
    }

    return (
        <Box sx={{ display: 'flex', padding: '6px', alignItems: 'center', borderBottom: `1px solid ${theme.palette.border1}` }}>
            <Box sx={{ width: 'auto', paddingRight: '12px' }}>
                {!opportunity.status && opportunity.source?.type !== 'email' && (
                    <Chip label='Draft' color='chip-request-new' />
                )}
                {!opportunity.status && opportunity.source?.type === 'email' && (
                    <Chip label='Draft from Mailbox' color='chip-request-new' />
                )}
                {statusToText({ opportunity }) === 'Sending...' && (
                    <Chip
                        label='Sending...'
                        color='chip-sent-to-endcustomer'
                        icon={<CircularProgress size={14} sx={{ color: 'inherit' }} />}
                    />
                )}
                {statusToText({ opportunity }) === 'Sent to Customer' && (
                    <Chip label='Sent' color='chip-sent-to-endcustomer' />
                )}
                {statusToText({ opportunity }) === 'Opened by Customer' && (
                    <Chip label='Opened' color='chip-opened-by-customer' />
                )}
                {statusToText({ opportunity }) === 'Accepted by Customer' && (
                    <Chip label='Accepted' color='chip-accepted' />
                )}
                {statusToText({ opportunity }) === 'Won' && (
                    <Chip label='Won' color='chip-won' />
                )}
                {statusToText({ opportunity }) === 'Rejected by Customer' && (
                    <Chip label='Rejected' color='chip-lost' />
                )}
                {statusToText({ opportunity }) === 'Lost' && (
                    <Chip label='Lost' color='chip-lost' />
                )}
            </Box>
            <Box sx={{ width: 'auto', paddingRight: '12px' }}>
                {(() => {
                    const pTag = (opportunity.tags || []).find(t => t.label?.startsWith('Pipeline: '));
                    const stage = pTag ? pTag.label.replace('Pipeline: ', '') : 'New Inquiry';
                    const colors = {
                        'New Inquiry': '#64748b', 'Cost Research': '#a16207', 'Ready for Sales': '#0891b2',
                        'Pending Customer': '#7c3aed', 'Follow-Up': '#db2777', 'Closed': '#475569',
                    };
                    const color = colors[stage] || '#64748b';
                    return <Chip label={stage} size='small' sx={{ fontSize: '11px', fontWeight: 600, color: '#fff', backgroundColor: color }} />;
                })()}
            </Box>
            <Box>
                <Button variant='text' color='primary' sx={{ color: '#2185D0', textDecoration: 'underline' }} onClick={() => onOpen({ opportunity })}>
                    <Typography variant='h6' sx={{ textDecoration: 'none', fontWeight: 700 }}>
                        {account({ opportunity, accounts })?.name || ''}
                    </Typography>
                </Button>
            </Box>
            <Box sx={{ flex: 1 }} />
            {!isNarrow && (
                <Box sx={{ borderLeft: `1px solid ${theme.palette.border1}`}} >
                    <Button sx={{ display: 'flex', alignItems: 'center', textTransform: 'none', color: theme.palette.text.secondary }} onClick={() => onOpen({ opportunity })}>
                        &nbsp;
                        <FontAwesomeIcon icon={faHouse} />
                        &nbsp;
                        &nbsp;
                        <Typography variant='caption'>
                            Details
                        </Typography>
                        &nbsp;
                    </Button>
                </Box>
            )}
            <Box sx={{ borderLeft: `1px solid ${theme.palette.border1}` }} >
                <Button sx={{ display: 'flex', alignItems: 'center', flexDirection: isNarrow ? 'column' : 'row', gap: 1, textTransform: 'none', color: theme.palette.text.secondary }} onClick={() => onOpen({ opportunity })}>
                    {!isNarrow && (
                        <>
                            <Typography variant='caption' sx={{ paddingRight: '12px' }}>
                                {opportunity.type === 'contract' ? 'Contract' : 'Price Quote'}
                            </Typography>
                        </>
                    )}
                    {isNarrow && (
                        <Typography variant='caption'>
                            {opportunity.type === 'contract' ? 'Contract' : 'Price Quote'}
                        </Typography>
                    )}
                </Button>
            </Box>
            {onUpdateOpportunity && !isNarrow && (
                <Box sx={{ borderLeft: `1px solid ${theme.palette.border1}` }} >
                    &nbsp;
                    &nbsp;
                    <Tooltip title={canChangeStatus ? '' : ownerOnly('Lost')}><span>
                    <Button variant='outlined' color='monada-button' disabled={!canChangeStatus || markingLost || opportunity.status === 'lost'} sx={{ textTransform: 'none', minWidth: '80px' }} onClick={() => setAskLostReason(true)}>
                        {markingLost ?
                            <CircularProgress size={24} /> :
                            <>
                                <FontAwesomeIcon icon={faCircleXmark} />
                                &nbsp;
                                &nbsp;
                                <Typography variant='caption'>
                                    Lost
                                </Typography>
                            </>
                        }
                    </Button>
                    </span></Tooltip>
                    &nbsp;
                    &nbsp;
                    <Tooltip title={canChangeStatus ? '' : ownerOnly('Won')}><span>
                    <Button variant='contained' color='monada-button' disabled={!canChangeStatus || markingWon || opportunity.status === 'completed'} sx={{ textTransform: 'none', minWidth: '135px' }} onClick={() => onMarkWon()}>
                        {markingWon ?
                            <CircularProgress size={24} /> :
                            <>
                                <FontAwesomeIcon icon={faCircleCheck} />
                                &nbsp;
                                &nbsp;
                                <Typography variant='caption'>
                                    Mark as Won
                                </Typography>
                            </>
                        }
                    </Button>
                    </span></Tooltip>
                    &nbsp;
                    &nbsp;
                </Box>
            )}

            <Snackbar open={message.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity='error' sx={{ width: '100%' }}>
                    {message.message}
                </Alert>
            </Snackbar>

            <LostReasonDialog
                open={askLostReason}
                working={markingLost}
                onClose={() => setAskLostReason(false)}
                onConfirm={onMarkLost}
                offers={opportunity.offers}
            />

            {pickOffers && (
                <PickOffersAndMarkWonDialog 
                    opportunity={opportunity} 
                    defaultOffer={opportunity.offers.find(o => opportunity.selected?.includes(o.id))?.id}
                    onNewOpportunity={(props) => {
                        onUpdateOpportunity(props);
                    }}
                    organizationId={opportunity.organizationId}
                    accesstoken={accesstoken} 
                    onError={e => {
                        setMessage({ open: true, message: statusError(e, 'Won') });
                    }} 
                    onClose={() => setPickOffers(false)}
                />
            )}
        </Box>
    );
}

function DescriptionToNode({ opportunity }) {
    const { description, transportationMethod } = opportunity;

    const routes = (() => {
        if (opportunity.type === 'one-off' || opportunity.type === 'one-off-email' || opportunity.type === 'one-off-free-text') return opportunity.routes;
        if (opportunity.type === 'contract') return _.uniqBy(opportunity.offers.map(o => o.routes[0]), o => JSON.stringify(o));
    })();

    if (!_.isEmpty(routes)) {
        return (
            <>
                <Box>
                    <Typography variant='body1' sx={{ padding: '6px', fontWeight: 500 }} >
                        {transportationMethod === 'air' && <FontAwesomeIcon icon={faPlane} />}
                        {transportationMethod === 'sea' && <FontAwesomeIcon icon={faShip} />}
                        {transportationMethod === 'land' && <FontAwesomeIcon icon={faTruck} />}
                        &nbsp;
                        &nbsp;
                        {routes[0].source?.text || 'Everywhere'} to {routes[0].destination?.text || 'Everywhere'}
                    </Typography>
                    {routes.length > 1 && (
                        <div style={{ padding: '0px 6px' }}>
                            <Typography variant='caption'>
                                {routes.map((r,i) => {
                                    if (i === 5 && routes.length > 6) {
                                        return <span>, and {routes.length - i} more</span>;
                                    }

                                    if (i > 5 && routes.length > 6) {
                                        return null;
                                    }

                                    return (
                                        <span key={JSON.stringify(r)} style={{ display: i === 0 ? 'none' : '' }}>
                                            {`${i === 1 ? 'and ' : ', '} ${r.source?.text || 'Everywhere'} to ${r.destination?.text || 'Everywhere'}`}
                                        </span>
                                    );
                               })}
                            </Typography>
                        </div>
                    )}
                </Box>
                {(description || {}).freeText && (
                    <Box sx={{ padding: '6px', fontSize: '12px' }} >
                        {description.freeText}
                    </Box>
                )}
            </>
        );
    }

    if (description?.freeText) {
        return (
            <Typography variant='body1' sx={{ padding: '6px', fontWeight: 500 }} >
                {transportationMethod === 'air' && <FontAwesomeIcon icon={faPlane} />}
                {transportationMethod === 'sea' && <FontAwesomeIcon icon={faShip} />}
                {transportationMethod === 'land' && <FontAwesomeIcon icon={faTruck} />}
                &nbsp;
                &nbsp;
                {description.freeText}
            </Typography>
        )
    }

    return (
        <Typography variant='body1' sx={{ padding: '6px', fontWeight: 500 }} >
            {transportationMethod === 'air' && <FontAwesomeIcon icon={faPlane} />}
            {transportationMethod === 'sea' && <FontAwesomeIcon icon={faShip} />}
            {transportationMethod === 'land' && <FontAwesomeIcon icon={faTruck} />}
        </Typography>
    );
}

function account({ opportunity, accounts}) {
    return accounts.find(a => a.id === opportunity.account?.id);
}